import React, { useEffect, useState, useContext } from 'react';
import { Link } from 'react-router-dom';
import { AuthContext } from '../AuthContext';
import { useTheme } from '../ThemeContext';
import getExchange from "../../MongoDb/GetExchange";
import { url as API_BASE_URL } from "../../MongoDb/MongoConstants";
import '../pages/Exchange.css';
import './SecuritiesDashboard.css';
import BrainExLogo from '../../images/BrainEx_Text.PNG';

function SecuritiesDashboard() {
    const { user } = useContext(AuthContext);
    const { isDarkMode } = useTheme();
    const [securities, setSecurities] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedSport, setSelectedSport] = useState('ALL');
    const [sortConfig, setSortConfig] = useState({ key: 'current_price', direction: 'desc' });
    const [manualOdds, setManualOdds] = useState({});
    const [editingSymbol, setEditingSymbol] = useState(null);
    const [confirmModal, setConfirmModal] = useState({ open: false, symbol: null, odds: null });
    const [passwordEntered, setPasswordEntered] = useState(false);
    const [inputPassword, setInputPassword] = useState('');
    const [activeTab, setActiveTab] = useState('securities');
    const [allUsers, setAllUsers] = useState([]);
    const [deposits, setDeposits] = useState([]);
    const [depositsLoading, setDepositsLoading] = useState(false);
    
    const fetchAllUsersData = async () => {
        try {
            const response = await fetch(`${API_BASE_URL}/api/user/all/portfolios`);
            const result = await response.json();
            if (result.success) {
                setAllUsers(result.data);
            }
        } catch (error) {
            console.error('Failed to fetch users data:', error);
        }
    };

    const fetchSecuritiesData = async () => {
        try {
            const exchange = await getExchange();
            
            // Fetch all user orders to find b589's open sell orders
            let allOrders = [];
            try {
                // Try to fetch all orders from the exchange
                const ordersResponse = await fetch(`${API_BASE_URL}/api/exchange/orders`);
                const ordersData = await ordersResponse.json();
                if (ordersData.success) {
                    allOrders = ordersData.data;
                }
            } catch (error) {
                console.warn('Could not fetch all orders:', error);
                allOrders = [];
            }

            // Create a map of symbol -> shares offered by b589
            const sharesOfferedMap = {};
            const sharesVolumeMap = {};
            const sharesOutstandingMap = {};
            
            allOrders.forEach(order => {
                // Get the symbol - try multiple field names
                const symbol = order.symbol || order.security_id;
                
                // Skip if no symbol
                if (!symbol) {
                    console.warn('Order missing symbol field:', order);
                    return;
                }
                
                const orderType = order.type || order.order_type;
                
                // Count shares outstanding (all filled orders, any user, any type)
                if ((order.status === 'filled' || order.status === 'partial' || order.status === 'cancelled') && orderType === 'sell' && order.user_id.endsWith('b589')) {
                    const filledQty = order.filled_quantity || 0;
                    if (filledQty > 0) {
                        if (!sharesVolumeMap[symbol]) {
                            sharesVolumeMap[symbol] = 0;
                        }
                        sharesVolumeMap[symbol] += filledQty;
                    }
                }
                
                // Only count sell orders from user ending in b589 that are open, partial, or filled
                if (
                    orderType === 'sell' && 
                    order.user_id && 
                    order.user_id.endsWith('b589') && 
                    (order.status === 'open' || order.status === 'partial' || order.status === 'filled' || order.status === 'cancelled')
                ) {
                    const filledQty = order.filled_quantity || 0;

                    if (order.status !== 'cancelled') {
                        const totalQty = order.quantity || 0;
                        const availableQty = totalQty;

                        if (availableQty > 0) {
                            if (!sharesOfferedMap[symbol]) {
                                sharesOfferedMap[symbol] = 0;
                            }
                            sharesOfferedMap[symbol] += availableQty;
                        }
                    } else if (filledQty > 0) {
                        if (!sharesOfferedMap[symbol]) {
                            sharesOfferedMap[symbol] = 0;
                        }
                        sharesOfferedMap[symbol] += filledQty;
                    }

                    if (!sharesOutstandingMap[symbol]) {
                        sharesOutstandingMap[symbol] = 0;
                    }

                    sharesOutstandingMap[symbol] += filledQty;
                }
            });

            console.log('All Orders:', allOrders);
            console.log('Shares Offered Map:', sharesOfferedMap);
            console.log('Sample exchange team symbols:', exchange.slice(0, 5).map(t => t.team_symbol));
            
            const enrichedSecurities = exchange.map(team => {
                // Calculate Fair Market Value for current odds
                let fmv = null;
                let fmvHigh = null;
                let fmvDiff = null;
                
                if (team.current_odds !== null && team.current_odds !== undefined & team.current_odds !== "OFF") {
                    const odds = team.current_odds;
                    let impliedProb;
                    if (odds > 0) {
                        impliedProb = 100 / (odds + 100);
                    } else {
                        impliedProb = (-odds) / (-odds + 100);
                    }
                    fmv = 1000 * impliedProb;
                }
                
                // Calculate Fair Market Value for high odds
                if (team.high_odds !== null && team.high_odds !== undefined && team.current_odds !== "OFF" && team.current_odds !== null && team.current_odds !== undefined) {
                    const highOdds = team.high_odds;
                    let impliedProb;
                    if (highOdds > 0) {
                        impliedProb = 100 / (highOdds + 100);
                    } else {
                        impliedProb = (-highOdds) / (-highOdds + 100);
                    }
                    fmvHigh = 1000 * impliedProb;
                }
                
                // Calculate difference between high FMV and current FMV
                if (fmv !== null && fmvHigh !== null & team.current_odds !== "OFF" && team.current_odds !== null && team.current_odds !== undefined) {
                    fmvDiff = fmv - fmvHigh;
                }
                
                let fmvPercent = null;
                if (fmvDiff !== null && fmvHigh !== null && fmvHigh !== 0 && team.current_odds !== "OFF" && team.current_odds !== null && team.current_odds !== undefined) {
                    fmvPercent = (fmvDiff / fmvHigh) * 100;
                }
                return {
                    ...team,
                    fmv,
                    fmvDiff,
                    fmvHigh,
                    fmvPercent,
                    sharesOffered: sharesOfferedMap[team.team_symbol] || 0,
                    sharesVolume: sharesVolumeMap[team.team_symbol] || 0,
                    sharesOutstanding: sharesOutstandingMap[team.team_symbol] || 0
                };
            });
            
            setSecurities(enrichedSecurities);
            setLoading(false);
        } catch (error) {
            console.error('Failed to fetch securities data:', error);
            setLoading(false);
        }
    };
    const fetchDeposits = async () => {
        setDepositsLoading(true);
        try {
            const response = await fetch(`${API_BASE_URL}/api/deposits`);
            const data = await response.json();
            console.log('Deposits API response:', data);
            setDeposits(data.transactions || []);
        } catch (error) {
            console.error('Failed to fetch deposits:', error);
        } finally {
            setDepositsLoading(false);
        }
    };

    useEffect(() => {
        fetchSecuritiesData();
    }, []);

    useEffect(() => {
        if (activeTab === 'users') {
            fetchAllUsersData();
        } else if (activeTab === 'deposits') {
            fetchDeposits();
        }
    }, [activeTab]);

    // Get unique sports
    const sports = ['ALL', ...new Set(securities.map(s => s.sport))].sort((a, b) => {
        if (a === 'ALL') return -1;
        if (b === 'ALL') return 1;
        return a.localeCompare(b);
    });

    // Filter by sport
    const filteredSecurities = selectedSport === 'ALL'
        ? securities
        : securities.filter(s => s.sport === selectedSport);

    // Sort data
    const sortedSecurities = [...filteredSecurities].sort((a, b) => {
        const aVal = a[sortConfig.key] ?? 0;
        const bVal = b[sortConfig.key] ?? 0;
        
        if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
        if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
        return 0;
    });

    const handleSort = (key) => {
        setSortConfig({
            key,
            direction: sortConfig.key === key && sortConfig.direction === 'desc' ? 'asc' : 'desc'
        });
    };

    const handleConfirmOdds = async (symbol, odds) => {
        if (!odds || odds === '') {
            setEditingSymbol(null);
            return;
        }

        // Show confirmation modal instead of directly updating
        setConfirmModal({ open: true, symbol, odds: parseInt(odds) });
    };

    const handleConfirmOddsSubmit = async () => {
        const { symbol, odds } = confirmModal;
        
        try {
            const response = await fetch(`${API_BASE_URL}/api/exchange/update-odds/${symbol}`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ odds })
            });

            const result = await response.json();
            if (result.success) {
                console.log(`Successfully updated odds for ${symbol}`);
                setManualOdds({
                    ...manualOdds,
                    [symbol]: ''
                });
                setEditingSymbol(null);
                setConfirmModal({ open: false, symbol: null, odds: null });
            } else {
                console.error('Failed to update odds:', result.error);
            }
        } catch (error) {
            console.error('Error updating odds:', error);
        }
    };

    const handleCancelOdds = () => {
        setConfirmModal({ open: false, symbol: null, odds: null });
    };

    const SortHeader = ({ label, sortKey, className }) => (
        <th 
            onClick={() => handleSort(sortKey)}
            className={`sortable-header ${sortConfig.key === sortKey ? 'active' : ''} ${className || ''}`}
            style={{ cursor: 'pointer', userSelect: 'none' }}
        >
            {label}
            {sortConfig.key === sortKey && (
                <span style={{ marginLeft: '4px' }}>
                    {sortConfig.direction === 'desc' ? '▼' : '▲'}
                </span>
            )}
        </th>
    );

    if (!user) {
        return (
            <div className="password-container">
                <h2>
                    Please <Link to="/login">log in</Link> to view securities.
                </h2>
            </div>
        );
    }

    if (!passwordEntered) {
        return (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', flexDirection: 'column' }}>
                <div style={{ color: 'white', textAlign: 'center', padding: '40px', border: '1px solid #ccc', borderRadius: '8px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
                    <h2>Enter Password</h2>
                    <input 
                        type="password" 
                        value={inputPassword}
                        onChange={(e) => setInputPassword(e.target.value)}
                        onKeyPress={(e) => e.key === 'Enter' && inputPassword === 'x' && setPasswordEntered(true)}
                        placeholder="Password"
                        style={{ padding: '8px', marginRight: '8px', width: '200px' }}
                    />
                    <button 
                        onClick={() => inputPassword === 'x' ? setPasswordEntered(true) : alert('Incorrect password')}
                        style={{ padding: '8px 16px' }}
                    >
                        Submit
                    </button>
                </div>
            </div>
        );
    }
    return (
        <>
            <div className="securities-dashboard-container">
                <div className="securities-header">
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '20px' }}>
                        <div>
                            <h1>Securities Dashboard</h1>
                            <p>View all securities with current odds and market data</p>
                        </div>
                        <button 
                            className="refresh-btn"
                            onClick={fetchSecuritiesData}
                            disabled={loading}
                            title="Refresh data"
                        >
                            <span style={{ display: 'inline-block', animation: loading ? 'spin 1s linear infinite' : 'none' }}>
                                🔄
                            </span>
                            {loading ? 'Refreshing...' : 'Refresh'}
                        </button>
                    </div>
                </div>
                {/* Tab Navigation */}
                <div style={{
                    display: 'flex',
                    gap: '8px',
                    marginBottom: '24px',
                    borderBottom: `2px solid ${isDarkMode ? '#444' : '#ddd'}`,
                }}>
                    <button
                        onClick={() => setActiveTab('securities')}
                        style={{
                            padding: '12px 24px',
                            background: activeTab === 'securities' ? 'var(--accent-primary)' : 'transparent',
                            color: activeTab === 'securities' ? 'white' : 'var(--text-secondary)',
                            border: 'none',
                            cursor: 'pointer',
                            fontSize: '1em',
                            fontWeight: activeTab === 'securities' ? '600' : '500',
                            borderBottom: activeTab === 'securities' ? '3px solid var(--accent-primary)' : 'none',
                            transition: 'all 0.2s ease'
                        }}
                    >
                        Securities
                    </button>
                    <button
                        onClick={() => setActiveTab('users')}
                        style={{
                            padding: '12px 24px',
                            background: activeTab === 'users' ? 'var(--accent-primary)' : 'transparent',
                            color: activeTab === 'users' ? 'white' : 'var(--text-secondary)',
                            border: 'none',
                            cursor: 'pointer',
                            fontSize: '1em',
                            fontWeight: activeTab === 'users' ? '600' : '500',
                            borderBottom: activeTab === 'users' ? '3px solid var(--accent-primary)' : 'none',
                            transition: 'all 0.2s ease'
                        }}
                    >
                        Users
                        </button>
                        <button
                            onClick={() => setActiveTab('deposits')}
                            style={{
                                padding: '12px 24px',
                                background: activeTab === 'deposits' ? 'var(--accent-primary)' : 'transparent',
                                color: activeTab === 'deposits' ? 'white' : 'var(--text-secondary)',
                                border: 'none',
                                cursor: 'pointer',
                                fontSize: '1em',
                                fontWeight: activeTab === 'deposits' ? '600' : '500',
                                borderBottom: activeTab === 'deposits' ? '3px solid var(--accent-primary)' : 'none',
                                transition: 'all 0.2s ease'
                            }}
                        >
                            Deposits
                        </button>
                    </div>

            {activeTab === 'securities' && (
                <>
                {/* Sport Filter */}
                <div className="securities-filters">
                    <div className="filter-group">
                        <label>Filter by Sport:</label>
                        <div className="sport-buttons">
                            {sports.map(sport => (
                                <button
                                    key={sport}
                                    className={`sport-btn ${selectedSport === sport ? 'active' : ''}`}
                                    onClick={() => setSelectedSport(sport)}
                                >
                                    {sport}
                                </button>
                            ))}
                        </div>
                    </div>
                </div>

                {loading ? (
                    <div className="text-center my-5">
                        <div className="spinner-border text-primary" role="status">
                            <span className="visually-hidden"></span>
                        </div>
                    </div>
                ) : (
                    <div className="securities-table-wrapper">
                        <table className="securities-table">
                            <thead>
                                <tr>
                                    <SortHeader label="Symbol" sortKey="team_symbol" />
                                    <SortHeader label="Team" sortKey="team" />
                                    <SortHeader label="Sport" sortKey="sport" />
                                    <SortHeader label="Current Price" sortKey="current_price" />
                                    <SortHeader label="Current Odds" sortKey="current_odds" />
                                    <th>Odds Provider</th>
                                    <SortHeader label="High Odds" sortKey="high_odds" />
                                    <th>High Provider</th>
                                    <SortHeader label="Current FMV" sortKey="fmv" />
                                    <SortHeader label="High FMV" sortKey="fmvHigh" />
                                    <SortHeader label="FMV Diff" sortKey="fmvDiff" />
                                    <SortHeader label="FMV %" sortKey="fmvPercent" />
                                    <SortHeader label="Shares Offered (b589)" sortKey="sharesOffered" />
                                    <SortHeader label="Shares Outstanding" sortKey="sharesOutstanding" />
                                    <SortHeader label="Volume" sortKey="sharesVolume" />
                                    <th>Manual Odds Override</th>  
                                </tr>
                            </thead>
                            <tbody>
                                {sortedSecurities.map((security, idx) => (
                                    <tr 
                                        key={security._id || idx}
                                        className="securities-row"
                                        onClick={() => window.location.href = `/exchange/${encodeURIComponent(security.team_symbol)}`}
                                        style={{ cursor: 'pointer' }}
                                    >
                                        <td className="symbol-cell">
                                            <strong>{security.team_symbol}</strong>
                                        </td>
                                        <td>{security.team || '--'}</td>
                                        <td><span className="sport-badge">{security.sport}</span></td>
                                        <td className="price-cell">
                                            ${security.current_price ? security.current_price.toFixed(2) : '0.00'}
                                        </td>
                                        <td className="odds-cell">
                                            {security.current_odds !== null && security.current_odds !== undefined
                                                ? `${security.current_odds > 0 ? '+' : ''}${security.current_odds.toLocaleString()}`
                                                : '--'}
                                        </td>
                                        <td className="provider-cell">
                                            {security.odds_provider || 'N/A'}
                                        </td>
                                        <td className="odds-cell">
                                            {security.high_odds !== null && security.high_odds !== undefined
                                                ? `${security.high_odds > 0 ? '+' : ''}${security.high_odds.toLocaleString()}`
                                                : '--'}
                                        </td>
                                        <td className="provider-cell">
                                            {security.high_provider || 'N/A'}
                                        </td>
                                        <td className="fmv-cell">
                                            {security.fmv !== null
                                                ? `$${security.fmv.toFixed(2)}`
                                                : '--'}
                                        </td>
                                        <td className="fmv-cell">
                                            {security.fmvHigh !== null
                                                ? `$${security.fmvHigh.toFixed(2)}`
                                                : '--'}
                                        </td>
                                        <td className="fmv-diff-cell" style={{ color: security.fmvDiff >= 0 ? '#28a745' : '#dc3545' }}>
                                            {security.fmvDiff !== null
                                                ? `${security.fmvDiff >= 0 ? '+' : ''}$${security.fmvDiff.toFixed(2)}`
                                                : '--'}
                                        </td>
                                        <td className="fmv-diff-cell" style={{ color: security.fmvDiff >= 0 ? '#28a745' : '#dc3545' }}>
                                            {security.fmvDiff !== null && security.fmvHigh !== null && security.fmvHigh !== 0
                                                ? `${security.fmvDiff >= 0 ? '+' : ''}${((security.fmvDiff / security.fmvHigh) * 100).toFixed(2)}%`
                                                : '--'}
                                        </td>
                                        <td className="shares-offered-cell">
                                            {security.sharesOffered > 0 ? security.sharesOffered : '--'}
                                        </td>
                                        <td className="shares-outstanding-cell">
                                            {security.sharesOutstanding > 0 ? security.sharesOutstanding : '--'}
                                        </td>
                                        <td className="shares-volume-cell">
                                            {security.sharesVolume > 0 ? security.sharesVolume : '--'}
                                        </td>
                                        <td className="manual-odds-cell">
                                            <div className="odds-input-container">
                                                <input
                                                    type="number"
                                                    placeholder="Enter odds"
                                                    value={manualOdds[security.team_symbol] || ''}
                                                    onChange={(e) => {
                                                        setManualOdds({
                                                            ...manualOdds,
                                                            [security.team_symbol]: e.target.value
                                                        });
                                                        setEditingSymbol(security.team_symbol);
                                                    }}
                                                    onClick={(e) => e.stopPropagation()}
                                                    className="manual-odds-input"
                                                />
                                                {editingSymbol === security.team_symbol && (
                                                    <button
                                                        className="confirm-odds-btn"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            handleConfirmOdds(security.team_symbol, manualOdds[security.team_symbol]);
                                                        }}
                                                    >
                                                        ✓
                                                    </button>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                        {sortedSecurities.length === 0 && (
                            <div className="no-data-message">
                                No securities found for the selected sport.
                            </div>
                        )}
                    </div>
                )}

                <div className="securities-footer">
                    <p>Total Securities: {sortedSecurities.length}</p>
                </div>
              </>
            )}
        
            {/* Users Tab Content */}
            {activeTab === 'users' && (
                <>
                    {loading ? (
                        <div className="text-center my-5">
                            <div className="spinner-border text-primary" role="status">
                                <span className="visually-hidden"></span>
                            </div>
                        </div>
                    ) : (
                        <div className="securities-table-wrapper">
                            <table className="securities-table">
                                <thead>
                                    <tr>
                                        <th>Email</th>
                                        <th>Name</th>
                                        <th>Exchange Available</th>
                                        <th>Exchange NAV</th>
                                        <th>Fund Available</th>
                                        <th>Fund NAV</th>
                                        <th>Portfolio Holdings</th>
                                        <th>Deposits</th>
                                        <th>Created</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {allUsers.map((user, idx) => {
                                        const userDeposits = deposits
                                            .filter(dep => dep.user_id === user._id)
                                            .reduce((sum, dep) => sum + (dep.amount || 0), 0);

                                        return (
                                        <tr key={user._id || idx}>
                                            <td className="symbol-cell">
                                                <strong>{user.email}</strong>
                                            </td>
                                            <td>{user.name || '--'}</td>
                                            <td className="price-cell">
                                                ${typeof user.exchange_avail === 'number' ? user.exchange_avail.toFixed(2) : '0.00'}
                                            </td>
                                            <td className="fmv-cell">
                                                ${typeof user.exchange_nav === 'number' ? user.exchange_nav.toFixed(2) : '0.00'}
                                            </td>
                                            <td className="price-cell">
                                                ${typeof user.fund_avail === 'number' ? user.fund_avail.toFixed(2) : '0.00'}
                                            </td>
                                            <td className="fmv-cell">
                                                ${typeof user.fund_nav === 'number' ? user.fund_nav.toFixed(2) : '0.00'}
                                            </td>
                                            <td>
                                                {user.portfolio && user.portfolio.length > 0 ? (
                                                    <div style={{ fontSize: '0.85em', maxHeight: '100px', overflowY: 'auto' }}>
                                                        {user.portfolio.map((holding, hidx) => (
                                                            <div key={hidx}>
                                                                {holding.symbol}: {holding.quantity} @ ${holding.avg_price.toFixed(2)}
                                                            </div>
                                                        ))}
                                                    </div>
                                                ) : (
                                                    '--'
                                                )}
                                            </td>
                                            <td>
                                                {userDeposits > 0
                                                    ? `$${userDeposits.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                                                    : '--'}
                                            </td>
                                            <td style={{ fontSize: '0.9em' }}>
                                                {user.createdAt ? new Date(user.createdAt).toLocaleDateString('en-US', { year: '2-digit', month: 'numeric', day: 'numeric' }) : '--'}
                                            </td>
                                        </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                            {allUsers.length === 0 && (
                                <div className="no-data-message">
                                    No users found.
                                </div>
                            )}
                        </div>
                    )}
                    <div className="securities-footer">
                        <p>Total Users: {allUsers.length}</p>
                    </div>
                </>
            )}
            {activeTab === 'deposits' && (
            <div>
                {depositsLoading ? (
                <div>Loading...</div>
                ) : (
                <table className="securities-table">
                    <thead>
                    <tr>
                        <th>Amount</th>
                        <th>Transaction ID</th>
                        <th>Payment Method</th>
                        <th>Payer Email</th>
                        <th>Status</th>
                        <th>Created At</th>
                    </tr>
                    </thead>
                    <tbody>
                    {deposits.map((dep, idx) => (
                        <tr key={dep._id || idx}>
                        <td>
                        {typeof dep.amount === 'number'
                            ? `$${dep.amount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                            : '--'}
                        </td>
                        <td>{dep.transaction_id}</td>
                        <td>{dep.payment_method}</td>
                        <td>{dep.payer_email}</td>
                        <td>{dep.status}</td>
                        <td>{dep.created_at ? new Date(dep.created_at).toLocaleString() : '--'}</td>
                        </tr>
                    ))}
                    </tbody>
                </table>
                )}
                <div className="securities-footer">
                    <p>Total Deposits: {deposits.length}</p>
                    <p>
                        Total Amount: ${deposits
                            .reduce((sum, dep) => sum + (dep.amount || 0), 0)
                            .toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </p>
                </div>
            </div>
            )}
            
            </div>
            
            {confirmModal.open && (
                <div className="modal-overlay" onClick={handleCancelOdds}>
                    <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                        <h3>Confirm Odds Change</h3>
                        <p>
                            Are you sure you want to update odds for <strong>{confirmModal.symbol}</strong> to <strong>{confirmModal.odds}</strong>?
                        </p>
                        <div className="modal-buttons">
                            <button 
                                className="btn btn-secondary"
                                onClick={handleCancelOdds}
                            >
                                Cancel
                            </button>
                            <button 
                                className="btn btn-primary"
                                onClick={handleConfirmOddsSubmit}
                            >
                                Confirm
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}

export default SecuritiesDashboard;